import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];

function EUR(n) {
  const s = (Number(n || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `€ ${s}`;
}

/**
 * Iter140j — Riepilogo delle risposte al questionario cliente.
 * Mostrato dopo conferma d'ordine (Stripe + Bonifico) così il cliente
 * rivede subito i dati che ha appena fornito. Se il servizio non ha
 * questionario o se non ci sono risposte, non renderizza nulla.
 */
function QuestionnaireSummary({ service, answers, legacyAnswer }) {
  if (!service?.has_questionnaire) return null;
  const fields = service.questionnaire_fields || [];
  const rows = [];
  if (fields.length > 0) {
    for (const f of fields) {
      const v = answers?.[f.id];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      let display = "";
      if (f.type === "checkbox") {
        const opts = (f.options || []).reduce((m, o) => { m[String(o.id)] = o.label || o.id; return m; }, {});
        display = (Array.isArray(v) ? v : [v]).map((x) => opts[String(x)] || String(x)).join(", ");
      } else if (f.type === "select") {
        const opts = (f.options || []).reduce((m, o) => { m[String(o.id ?? o)] = o.label ?? o; return m; }, {});
        display = opts[String(v)] || String(v);
      } else {
        display = String(v);
      }
      rows.push({ label: f.label || f.id, value: display });
    }
  } else if (legacyAnswer && legacyAnswer.trim()) {
    rows.push({ label: "", value: legacyAnswer.trim() });
  }
  if (rows.length === 0) return null;
  return (
    <div
      className="mt-3 rounded-lg border border-purple-200 bg-purple-50 p-4 text-left"
      data-testid="checkout-questionnaire-summary"
    >
      <div className="text-[10px] uppercase tracking-widest text-purple-800 font-bold mb-2">
        Dati forniti
      </div>
      <dl className="space-y-1.5 text-sm">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap gap-x-2">
            {r.label && <dt className="text-slate-500 min-w-[120px]">{r.label}:</dt>}
            <dd className="text-slate-900 font-medium whitespace-pre-wrap">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function ClientCheckout() {
  const { pid } = useParams();
  const [info, setInfo] = useState(null);
  const [slotsData, setSlotsData] = useState({ slots: [] });
  const [slotMonth, setSlotMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() + 1 }; });
  const [selectedDate, setSelectedDate] = useState(null);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Diritti ente: informativa + checkbox obbligatoria per pratiche con oneri Ente
  const [entityFeesPolicyHtml, setEntityFeesPolicyHtml] = useState("");
  const [entityFeesAcked, setEntityFeesAcked] = useState(false);
  // 3-fasi checkout: "info" → "payment" → "calendar" (o "success" senza visita)
  const [phase, setPhase] = useState("info");
  const [bookingSlot, setBookingSlot] = useState(false);
  const [bookedSlot, setBookedSlot] = useState(null);  // dopo book-slot success
  // Iter63/64: risposta al questionario (singolo campo legacy o form multi-campo)
  const [questionnaireAnswer, setQuestionnaireAnswer] = useState("");
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
  // Iter133: metodo di pagamento scelto dal cliente. Popolato con l'id del
  // metodo (Stripe/Bonifico/altri) restituito da `/public/checkout/{pid}`.
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [offlineConfirmed, setOfflineConfirmed] = useState(null); // dettagli metodo dopo confirm-offline
  // Iter140w: modal di conferma tra selezione metodo e submit + flag di
  // "reindirizzamento Stripe in corso" per mostrare full-screen loader.
  const [confirmingMethod, setConfirmingMethod] = useState(null); // metodo in fase di conferma
  const [redirectingToStripe, setRedirectingToStripe] = useState(false);
  // Iter137: email cliente finale — obbligatoria per confermare l'ordine
  // (usata per invio ricevuta Stripe / istruzioni bonifico).
  const [clientEmail, setClientEmail] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/checkout/${pid}`)
      .then(r => {
        setInfo(r.data);
        // Iter133: preseleziona il primo metodo Stripe se disponibile, altrimenti il primo abilitato.
        const methods = Array.isArray(r.data?.payment_methods) ? r.data.payment_methods : [];
        const stripe = methods.find(m => m.type === "stripe");
        setSelectedMethod((stripe || methods[0])?.id || null);
        // Iter137: prepopola l'email cliente se già presente sulla pratica.
        if (r.data?.client_email) setClientEmail(r.data.client_email);
      })
      .catch(err => setError(err?.response?.data?.detail || "Link non valido o pratica non trovata"))
      .finally(() => setLoading(false));
  }, [pid]);

  useEffect(() => {
    axios.get(`${API}/public/entity-fees-policy`)
      .then(r => setEntityFeesPolicyHtml(r.data?.content_html || ""))
      .catch(() => setEntityFeesPolicyHtml(""));
  }, []);

  useEffect(() => {
    if (!info?.visit_on_first_step) return;
    axios.get(`${API}/public/checkout/${pid}/slots`, { params: { year: slotMonth.year, month: slotMonth.month } })
      .then(r => setSlotsData(r.data))
      .catch(() => setSlotsData({ slots: [] }));
  }, [pid, info, slotMonth]);

  const slotsByDate = useMemo(() => {
    const m = {};
    for (const s of (slotsData?.slots || [])) {
      if (!m[s.date]) m[s.date] = [];
      m[s.date].push(s);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.hour - b.hour);
    return m;
  }, [slotsData]);

  const monthGrid = useMemo(() => {
    const y = slotMonth.year, m = slotMonth.month;
    const first = new Date(y, m - 1, 1);
    const firstDayIdx = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDayIdx; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, date: dateStr, hasSlots: (slotsByDate[dateStr] || []).length > 0 });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [slotMonth, slotsByDate]);

  const slotsForSelectedDate = selectedDate ? (slotsByDate[selectedDate] || []) : [];
  const totalSlotsCount = (slotsData?.slots || []).length;

  const [pollingStatus, setPollingStatus] = useState(null); // 'pending' | 'paid' | 'failed'

  // Al ritorno da Stripe, se c'è ?session_id=... nella URL, polling status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setPollingStatus("pending");
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await axios.get(`${API}/payments/checkout/status/${sid}`);
        if (r.data?.payment_status === "paid") {
          setPollingStatus("paid");
          toast.success("Pagamento ricevuto! Grazie.");
          // Se pratica con visita E il Pro NON l'ha saltata, apri Fase 3.
          // Iter65: se `visit_skipped=True`, procedi direttamente a success.
          if (info?.visit_on_first_step && !info?.visit_skipped) {
            setTimeout(() => {
              setPollingStatus(null);
              setPhase("calendar");
              // Pulisci il session_id dall'URL
              window.history.replaceState({}, "", window.location.pathname);
            }, 1500);
          } else {
            setTimeout(() => {
              setPollingStatus(null);
              setPhase("success");
              window.history.replaceState({}, "", window.location.pathname);
            }, 1500);
          }
          return;
        }
        if (["expired", "failed", "cancelled"].includes(r.data?.payment_status)) {
          setPollingStatus("failed");
          return;
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else setPollingStatus("timeout");
      } catch {
        if (attempts < 8) setTimeout(poll, 2000);
        else setPollingStatus("timeout");
      }
    };
    poll();
  // eslint-disable-next-line
  }, []);

  // Auto-resume: se pratica è già pagata/autorizzata e manca lo slot,
  // salta direttamente a Fase 3 (Calendario) con badge "Servizio già pagato".
  useEffect(() => {
    if (!info) return;
    const paidAlready = info.payment_status === "paid" || info.payment_status === "authorized";
    const hasSlot = !!info.scheduled_appointment;
    // Iter65: se il Pro ha saltato il sopralluogo, il cliente non deve mai
    // vedere il calendario — dopo il pagamento va direttamente in success.
    if (paidAlready && info.visit_on_first_step && !hasSlot && !info.visit_skipped) {
      setPhase("calendar");
    } else if (paidAlready && (!info.visit_on_first_step || info.visit_skipped)) {
      setPhase("success");
    }
  }, [info]);

  const submit = async () => {
    // Iter137: email cliente obbligatoria PRIMA della scelta pagamento.
    if (!clientEmail.trim()) {
      toast.error("Inserisci la tua email per proseguire");
      return;
    }
    if (info.has_entity_fees && !entityFeesAcked) {
      toast.error("Devi confermare l'informativa sui diritti ente per proseguire");
      return;
    }
    // Iter63/64: validazione questionario. Se ci sono field, verifica required.
    if (info?.service?.has_questionnaire) {
      const fields = info.service.questionnaire_fields || [];
      if (fields.length > 0) {
        for (const f of fields) {
          const v = questionnaireAnswers[f.id];
          const isEmpty = f.type === "checkbox"
            ? !(Array.isArray(v) && v.length > 0)
            : !(v && String(v).trim());
          if (f.required && isEmpty) {
            toast.error(`Campo obbligatorio: ${f.label}`);
            return;
          }
        }
      } else if (!questionnaireAnswer.trim()) {
        toast.error("Rispondi alla domanda del questionario per proseguire");
        return;
      }
    }
    setSubmitting(true);
    try {
      const body = { origin_url: window.location.origin, client_email: clientEmail.trim() };
      if (info?.service?.has_questionnaire) {
        const fields = info.service.questionnaire_fields || [];
        if (fields.length > 0) {
          body.questionnaire_answers = questionnaireAnswers;
        } else {
          body.questionnaire_answer = questionnaireAnswer.trim();
        }
      }
      // Iter140s: in modalità saldo usa l'endpoint dedicato (nessun questionario).
      const endpoint = info?.balance_mode
        ? `${API}/public/checkout/${pid}/balance/confirm`
        : `${API}/public/checkout/${pid}/confirm`;
      const r = await axios.post(endpoint, body);
      const url = r.data.url || r.data.checkout_url;
      if (!url) throw new Error("URL Stripe mancante");
      setRedirectingToStripe(true);
      setTimeout(() => { window.location.href = url; }, 700);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il pagamento");
      setRedirectingToStripe(false);
    } finally { setSubmitting(false); }
  };

  // Iter137: la conferma bonifico è nuovamente esplicita — il cliente
  // clicca "Conferma", inviamo l'email al backend, mostriamo il box verde
  // con le istruzioni e inviamo email al cliente/admin lato server.
  const submitOffline = async () => {
    if (!clientEmail.trim()) { toast.error("Inserisci la tua email prima di confermare"); return; }
    if (!selectedMethod) { toast.error("Seleziona un metodo di pagamento"); return; }
    const body = { payment_method_id: selectedMethod, client_email: clientEmail.trim() };
    if (info?.service?.has_questionnaire) {
      const fields = info.service.questionnaire_fields || [];
      if (fields.length > 0) body.questionnaire_answers = questionnaireAnswers;
      else body.questionnaire_answer = questionnaireAnswer.trim();
    }
    setSubmitting(true);
    try {
      // Iter140s: se siamo in modalità saldo, usa l'endpoint dedicato.
      const endpoint = info?.balance_mode
        ? `${API}/public/checkout/${pid}/balance/confirm-offline`
        : `${API}/public/checkout/${pid}/confirm-offline`;
      const r = await axios.post(endpoint, {
        method_id: body.payment_method_id,
        payment_method_id: body.payment_method_id,
        client_email: body.client_email,
        ...(info?.balance_mode ? {} : {
          questionnaire_answers: body.questionnaire_answers,
          questionnaire_answer: body.questionnaire_answer,
        }),
      });
      setOfflineConfirmed(r.data.method);
      toast.success("Ordine confermato: controlla la tua email per le istruzioni.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la conferma");
    } finally { setSubmitting(false); }
  };

  const bookSlot = async () => {
    if (!chosenSlot) {
      toast.error("Seleziona una data e un orario");
      return;
    }
    setBookingSlot(true);
    try {
      await axios.post(`${API}/public/checkout/${pid}/book-slot`, {
        slot_date: chosenSlot.date,
        slot_hour: chosenSlot.hour,
        slot_minute: Number(chosenSlot.minute || 0),
        slot_operator_id: chosenSlot.best_operator.id,
      });
      setBookedSlot(chosenSlot);
      toast.success("Sopralluogo prenotato con successo!");
      setPhase("success");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la prenotazione dello slot");
    } finally {
      setBookingSlot(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-600 text-lg font-bold mb-2">Link non valido</div>
          <div className="text-sm text-slate-600">{error}</div>
        </div>
      </div>
    );
  }
  if (!info) return null;

  const paid = info.payment_status === "paid" || info.payment_status === "authorized" || pollingStatus === "paid";

  // Overlay post-Stripe: se abbiamo un session_id in URL, mostra stato
  if (pollingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md bg-white rounded-2xl shadow-lg p-10 text-center" data-testid="checkout-status-panel">
          {pollingStatus === "pending" && (
            <>
              <div className="text-2xl font-bold mb-3">Verifica pagamento…</div>
              <div className="text-sm text-slate-600">Stiamo confermando il tuo pagamento con Stripe. Non chiudere la pagina.</div>
              <div className="mt-6 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            </>
          )}
          {pollingStatus === "paid" && (
            <>
              <div className="text-3xl mb-2">🎉</div>
              <div className="text-2xl font-bold text-emerald-700 mb-3" data-testid="checkout-status-paid">Pagamento confermato</div>
              <div className="text-sm text-slate-600">Grazie! La tua pratica è ora in lavorazione. Riceverai aggiornamenti via email.</div>
            </>
          )}
          {(pollingStatus === "failed" || pollingStatus === "timeout") && (
            <>
              <div className="text-2xl font-bold text-red-700 mb-3">Pagamento non confermato</div>
              <div className="text-sm text-slate-600 mb-4">
                {pollingStatus === "timeout"
                  ? "Verifica in corso più lunga del previsto. Riceverai una email quando il pagamento sarà confermato."
                  : "La sessione è scaduta o è stata annullata. Puoi riprovare."}
              </div>
              <Button onClick={() => { window.location.href = window.location.pathname; }}>Riprova</Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">

      <header className="bg-white border-b py-4">
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Housetag" className="w-11 h-11 rounded-md" />
            <div>
              <div className="text-lg font-bold tracking-tight" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Housetag.it
              </div>
              <div className="text-xs text-slate-500">La gestione digitale dei tuoi immobili.</div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">Pratica {pid.slice(0, 8)}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {/* Progress indicator — Iter138: se il cliente ha selezionato un
            metodo NON-Stripe (bonifico ecc.) nascondiamo la fase Calendario
            perché per queste pratiche NON verrà prenotato alcuno slot: si
            passa direttamente in `awaiting_payment`. Il pallino resta solo
            per Stripe (servizio con sopralluogo → prenoti dopo il pagamento). */}
        {info.visit_on_first_step && !paid && (() => {
          const methods = Array.isArray(info?.payment_methods) ? info.payment_methods : [];
          const currentMethod = methods.find(m => m.id === selectedMethod);
          const skipCalendar = currentMethod && currentMethod.type !== "stripe";
          const steps = skipCalendar ? ["info", "payment"] : ["info", "payment", "calendar"];
          const labels = skipCalendar
            ? ["1. Info", "2. Pagamento"]
            : ["1. Info", "2. Pagamento", "3. Calendario"];
          return (
            <div className="flex items-center justify-center gap-2 text-xs" data-testid="checkout-phase-indicator">
              {steps.map((step, i) => {
                const isActive = phase === step;
                const isDone = steps.indexOf(phase) > i;
                return (
                  <React.Fragment key={step}>
                    <div className={`px-3 py-1.5 rounded-full font-semibold border-2 transition-all ${
                      isActive ? "bg-purple-600 text-white border-purple-600" :
                      isDone ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                      "bg-white text-slate-500 border-slate-200"
                    }`}>
                      {isDone ? "✓ " : ""}{labels[i]}
                    </div>
                    {i < steps.length - 1 && <div className="w-6 h-0.5 bg-slate-300" />}
                  </React.Fragment>
                );
              })}
            </div>
          );
        })()}
        {/* Vecchio progress indicator (Iter138 sostitutivo sopra) — mantenuto solo per fallback */}
        {false && info.visit_on_first_step && !paid && (
          <div className="flex items-center justify-center gap-2 text-xs" data-testid="checkout-phase-indicator">
            {["info", "payment", "calendar"].map((step, i) => {
              const stepLabel = ["1. Info", "2. Pagamento", "3. Calendario"][i];
              const isActive = phase === step;
              const isDone = ["info", "payment", "calendar"].indexOf(phase) > i;
              return (
                <React.Fragment key={step}>
                  <div className={`px-3 py-1.5 rounded-full font-semibold border-2 transition-all ${
                    isActive ? "bg-purple-600 text-white border-purple-600" :
                    isDone ? "bg-emerald-50 text-emerald-800 border-emerald-300" :
                    "bg-white text-slate-500 border-slate-200"
                  }`}>
                    {isDone && !isActive ? "✓ " : ""}{stepLabel}
                  </div>
                  {i < 2 && <div className={`h-0.5 w-6 ${isDone ? "bg-emerald-400" : "bg-slate-200"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {phase === "calendar" && paid && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded text-emerald-800" data-testid="checkout-already-paid">
            ✅ <strong>Servizio già pagato</strong> — ora ti manca solo prenotare il sopralluogo con un tecnico.
          </div>
        )}

        {phase === "success" && (
          <div className="bg-white border-2 border-emerald-300 rounded-2xl shadow-lg p-10 text-center" data-testid="checkout-success">
            <div className="text-5xl mb-3">🎉</div>
            <div className="text-2xl font-bold text-emerald-700 mb-3">
              {info.visit_on_first_step ? "Sopralluogo prenotato" : "Servizio attivato"}
            </div>
            <div className="text-sm text-slate-600 mb-4">
              {bookedSlot ? (
                <>Ti aspettiamo il <strong>{bookedSlot.date}</strong> alle <strong>{String(bookedSlot.hour).padStart(2, "0")}:{String(bookedSlot.minute || 0).padStart(2, "0")}</strong>. Riceverai una email di conferma con tutti i dettagli.</>
              ) : (
                <>Grazie! La tua pratica <strong>{info.service.name}</strong> è ora in lavorazione. Riceverai aggiornamenti via email.</>
              )}
            </div>
            {/* Iter140j: riepilogo delle risposte del questionario anche
                nella schermata di successo (post-Stripe / post-calendar). */}
            <QuestionnaireSummary
              service={info.service}
              answers={questionnaireAnswers}
              legacyAnswer={questionnaireAnswer}
            />
          </div>
        )}

        {/* Riepilogo servizio: sempre visibile nelle fasi info/payment/calendar */}
        {phase !== "success" && (
        <div className="bg-white border rounded-lg shadow-sm p-6 space-y-4" data-testid="checkout-summary">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-purple-800 font-bold">Servizio richiesto da {info.professional.insegna || "il tuo professionista"}</div>
            <h1 className="text-2xl font-bold mt-1" data-testid="checkout-svc-name">{info.service.name}</h1>
          </div>

          {/* Iter140j: `client_info` sempre visibile in ogni fase — non solo
              a inizio flusso — così il cliente sa sempre cosa sta comprando. */}
          {info.service.client_info && (
            <div className="p-3 bg-purple-50 border-l-2 border-purple-500 text-sm text-slate-800 whitespace-pre-wrap" data-testid="checkout-client-info">
              {info.service.client_info}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Intestatario</div>
              <div className="font-medium">{info.client_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Immobile</div>
              <div className="font-medium" data-testid="checkout-property">
                {info.property.address}, {info.property.city}
                {info.property.province ? ` (${info.property.province})` : ""}
              </div>
            </div>
          </div>

          <div className="pt-4 border-t space-y-1.5">
            {/* Iter140r: banner "modalità saldo" se la pratica è in awaiting_balance */}
            {info.balance_mode && (
              <div
                className="mb-2 p-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900"
                data-testid="checkout-balance-mode-banner"
              >
                💰 <strong>Modalità SALDO</strong> — Hai già pagato l'anticipo di € {(info.advance_amount || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Ora ti chiediamo il <strong>saldo residuo</strong> per ricevere i documenti finali.
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{info.balance_mode ? "Imponibile saldo" : "Imponibile"}</span>
              <span className="font-mono tabular-nums" data-testid="checkout-net">{EUR(info.price_net)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">IVA {info.vat_rate}%</span>
              <span className="font-mono tabular-nums" data-testid="checkout-vat">{EUR(info.vat_amount)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="checkout-delivery-time">
              <Clock className="w-4 h-4" />
              <span>Tempo stimato per la consegna: {info.service.delivery_time_days} giorni</span>
            </div>
            {info.has_entity_fees && !info.balance_mode && (
              <div className="flex items-center justify-between text-sm" data-testid="checkout-entity-fees-line">
                <span className="text-slate-600">Diritti ente</span>
                <span className="font-mono tabular-nums text-amber-700 font-medium">fatturati a parte</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-sm font-semibold">
                {info.balance_mode ? "Saldo da pagare (IVA inclusa)" : "Totale da pagare (IVA inclusa)"}
              </div>
              <div className="text-3xl font-bold tabular-nums" data-testid="checkout-total">{EUR(info.price_total_with_vat)}</div>
            </div>
          </div>
        </div>
        )}

        {/* FASE 1 - INFO: messaggio contestuale + bottone Procedi */}
        {phase === "info" && !paid && (
          <>
            <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded" data-testid="checkout-info-msg">
              <div className="text-sm text-slate-800">
                {info.visit_on_first_step
                  ? "Non preoccuparti, dopo il pagamento potrai prenotare il rilievo con un tecnico."
                  : "Abbiamo quasi finito, ti basta eseguire il pagamento per attivare il servizio."}
              </div>
            </div>

            {info.has_entity_fees && (
              <div className="bg-white border-2 border-amber-300 rounded-lg shadow-sm p-5 space-y-3" data-testid="checkout-entity-fees">
                <div className="flex items-center gap-2 text-amber-800 font-bold">
                  <span className="text-xl">💶</span>
                  <span>Diritti ente da pagare</span>
                </div>
                <div
                  className="text-sm text-slate-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: entityFeesPolicyHtml || "" }}
                  data-testid="checkout-entity-fees-content"
                />
                <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer pt-2 border-t">
                  <input
                    type="checkbox"
                    checked={entityFeesAcked}
                    onChange={(e) => setEntityFeesAcked(e.target.checked)}
                    className="mt-1"
                    data-testid="checkout-entity-fees-ack"
                  />
                  <span>
                    <strong>Ho capito</strong> — accetto quanto sopra. Il totale finale della pratica potrà includere gli oneri Ente comunicati separatamente.
                  </span>
                </label>
              </div>
            )}

            <div className="bg-white border rounded-lg shadow-sm p-6">
              <Button
                onClick={() => setPhase("payment")}
                disabled={info.has_entity_fees && !entityFeesAcked}
                className="w-full ht-gradient border-0 py-6 text-base font-semibold"
                data-testid="checkout-proceed-btn"
              >
                Procedi al pagamento →
              </Button>
            </div>
          </>
        )}

        {/* FASE 2 - PAGAMENTO: Stripe button */}
        {phase === "payment" && !paid && (
          <div className="bg-white border rounded-lg shadow-sm p-6 space-y-3">
            <div className="text-center text-xs uppercase tracking-widest text-slate-500 font-bold">Procedi al pagamento sicuro</div>
            {(info?.payment_methods || []).find((m) => m.id === selectedMethod)?.type
              && (info.payment_methods.find((m) => m.id === selectedMethod).type !== "stripe") ? null : (
            <div className="text-center text-sm text-slate-600 mb-3" data-testid="checkout-stripe-redirect-note">
              Verrai reindirizzato su Stripe per completare il pagamento in modo sicuro.
            </div>
            )}

            {/* Iter120: messaggio configurato dall'Admin, mostrato solo al cliente finale */}
            {info?.client_payment_message && (
              <div
                className="rounded-lg border-l-4 border-indigo-500 bg-indigo-50 p-4 text-sm text-indigo-900 whitespace-pre-wrap"
                data-testid="client-payment-message-box"
              >
                <div className="flex items-start gap-2">
                  <span className="text-lg leading-none shrink-0">💬</span>
                  <div>{info.client_payment_message}</div>
                </div>
              </div>
            )}

            {/* Iter63/64: Questionario cliente — visibile solo se il servizio lo prevede.
                Se sono definiti `questionnaire_fields`, renderizza un form multi-campo
                (Google-Forms style). Altrimenti fallback a singola textarea (iter63). */}
            {info?.service?.has_questionnaire && (() => {
              const fields = info.service.questionnaire_fields || [];
              const hasFields = fields.length > 0;
              const allRequiredFilled = !hasFields
                ? questionnaireAnswer.trim().length > 0
                : fields.every((f) => {
                    if (!f.required) return true;
                    const v = questionnaireAnswers[f.id];
                    if (f.type === "checkbox") return Array.isArray(v) && v.length > 0;
                    return v && String(v).trim().length > 0;
                  });
              return (
                <div
                  className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-left space-y-3"
                  data-testid="checkout-questionnaire"
                >
                  <div>
                    <div className="text-xs uppercase tracking-wide font-bold text-purple-800">
                      Domanda del professionista
                    </div>
                    {info.service.question_text && (
                      <div className="text-sm text-slate-800 whitespace-pre-line mt-1">
                        {info.service.question_text}
                      </div>
                    )}
                  </div>

                  {hasFields ? (
                    <div className="space-y-3">
                      {fields.map((f) => (
                        <QuestionnaireFieldInput
                          key={f.id}
                          field={f}
                          value={questionnaireAnswers[f.id]}
                          onChange={(v) => setQuestionnaireAnswers({ ...questionnaireAnswers, [f.id]: v })}
                        />
                      ))}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-semibold text-slate-700">
                        La tua risposta <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        value={questionnaireAnswer}
                        onChange={(e) => setQuestionnaireAnswer(e.target.value)}
                        rows={4}
                        maxLength={5000}
                        className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Scrivi qui la tua risposta…"
                        data-testid="checkout-questionnaire-input"
                        required
                      />
                      <div className="text-[10px] text-slate-500 text-right">
                        {questionnaireAnswer.length}/5000
                      </div>
                    </div>
                  )}
                  {!allRequiredFilled && (
                    <div className="text-[11px] text-amber-700 italic">
                      Compila i campi obbligatori (*) per procedere al pagamento.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Iter137: campo email cliente obbligatorio PRIMA della scelta
                pagamento. Serve per inviare la ricevuta Stripe / le
                istruzioni bonifico.
                Iter140w: nascosto quando l'ordine è confermato (bonifico) o
                in fase di reindirizzamento (Stripe) — l'utente non deve più
                interagire col form. */}
            {!offlineConfirmed && !redirectingToStripe && (
            <div className="space-y-1" data-testid="checkout-client-email-row">
              <label htmlFor="client-email" className="text-xs uppercase tracking-wide font-bold text-slate-600">
                La tua email <span className="text-red-600">*</span>
              </label>
              <input
                id="client-email"
                type="email"
                placeholder="es. mario@rossi.it"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className="w-full h-11 border-2 border-slate-200 focus:border-indigo-500 rounded-md px-3 text-sm bg-white outline-none transition"
                data-testid="checkout-client-email"
              />
              <div className="text-[11px] text-slate-500">
                Riceverai la ricevuta e le istruzioni di pagamento a questo indirizzo.
              </div>
            </div>
            )}

            {/* Iter133: elenco metodi di pagamento (mostra sempre almeno il tasto
                Stripe se il DB è vuoto — best-effort backward compat). Il
                cliente seleziona un metodo con la radio-card; il bottone in
                basso si adatta di conseguenza.
                Iter140w: nascosto quando l'ordine è confermato/in redirect. */}
            {!offlineConfirmed && !redirectingToStripe && (() => {
              const methods = Array.isArray(info?.payment_methods) ? info.payment_methods : [];
              if (methods.length === 0) return null;
              return (
                <div className="space-y-2" data-testid="checkout-payment-methods">
                  <div className="text-xs uppercase tracking-wide font-bold text-slate-600">
                    Scegli come pagare
                  </div>
                  {methods.map((m) => (
                    <label
                      key={m.id}
                      className={
                        "flex items-start gap-3 p-3 rounded-md border-2 cursor-pointer transition " +
                        (selectedMethod === m.id
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-slate-200 hover:border-slate-300")
                      }
                      data-testid={`checkout-payment-method-${m.type || m.id}`}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        checked={selectedMethod === m.id}
                        onChange={() => setSelectedMethod(m.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {/* Iter140w: NON renderizziamo `m.icon` come testo
                              (era una stringa "Bank"/"CreditCard" mostrata
                              accanto al nome). Il campo icon del DB è un
                              identificativo, non un emoji visualizzabile. */}
                          {m.name || m.type}
                        </div>
                        {m.instructions && (
                          <div className="text-[11px] text-slate-600 mt-1 whitespace-pre-wrap">
                            {m.instructions}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              );
            })()}

            {(() => {
              const methods = Array.isArray(info?.payment_methods) ? info.payment_methods : [];
              const currentMethod = methods.find(m => m.id === selectedMethod);
              // Se non ci sono metodi caricati dal server, fallback al vecchio comportamento (Stripe hardcoded)
              const isStripe = !currentMethod || currentMethod.type === "stripe";
              // Iter137: se abbiamo appena confermato un ordine offline, mostra
              // il box verde con IBAN + messaggio "servizio parte alla ricezione bonifico".
              if (offlineConfirmed) {
                return (
                  <div
                    className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-5 text-center space-y-3"
                    data-testid="checkout-offline-confirmed"
                  >
                    <div className="text-3xl">✅</div>
                    <div className="text-base font-bold text-emerald-900">
                      Ordine confermato con {offlineConfirmed.name}
                    </div>
                    {offlineConfirmed.instructions && (
                      <div className="text-sm text-slate-800 text-left bg-white border rounded p-3 whitespace-pre-wrap">
                        {offlineConfirmed.instructions}
                      </div>
                    )}
                    <div className="text-xs text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-3 py-2">
                      📩 Ti abbiamo inviato le stesse informazioni via email a <strong>{clientEmail}</strong>.
                    </div>
                    {/* Iter140j: riepilogo delle risposte del questionario appena
                        inserite, così il cliente rivede subito i dati forniti. */}
                    <QuestionnaireSummary
                      service={info.service}
                      answers={questionnaireAnswers}
                      legacyAnswer={questionnaireAnswer}
                    />
                    {!(offlineConfirmed.type === "cash" && offlineConfirmed.instructions) && (
                    <div className="text-xs text-slate-700 italic" data-testid="checkout-offline-note">
                      {offlineConfirmed.type === "bank_transfer"
                        ? "Ti ricordiamo che il servizio partirà dalla ricezione del bonifico."
                        : offlineConfirmed.type === "cash"
                          ? "Pagherai al ritiro del documento."
                          : "Ti ricordiamo che il servizio partirà dalla ricezione del pagamento."}
                    </div>
                    )}
                  </div>
                );
              }
              // Iter140w: se stiamo reindirizzando a Stripe, mostra un
              // loader full-width al posto dei bottoni.
              if (redirectingToStripe) {
                return (
                  <div
                    className="rounded-lg border-2 border-indigo-300 bg-indigo-50 p-6 text-center space-y-2"
                    data-testid="checkout-stripe-redirecting"
                  >
                    <div className="text-3xl animate-pulse">💳</div>
                    <div className="text-base font-bold text-indigo-900">
                      Ti stiamo reindirizzando al pagamento sicuro…
                    </div>
                    <div className="text-xs text-indigo-700">
                      Attendere qualche secondo. Sarai portato sulla pagina Stripe.
                    </div>
                  </div>
                );
              }
              return isStripe ? (
                <Button
                  onClick={() => setConfirmingMethod(currentMethod || { id: "stripe", type: "stripe", name: "Carta di credito/debito" })}
                  disabled={submitting || !clientEmail.trim() || (info?.service?.has_questionnaire && (() => {
                    const fields = info.service.questionnaire_fields || [];
                    if (fields.length === 0) return !questionnaireAnswer.trim();
                    return fields.some((f) => {
                      if (!f.required) return false;
                      const v = questionnaireAnswers[f.id];
                      if (f.type === "checkbox") return !(Array.isArray(v) && v.length > 0);
                      return !(v && String(v).trim());
                    });
                  })())}
                  className="w-full ht-gradient border-0 py-6 text-base font-semibold"
                  data-testid="checkout-pay-btn"
                >
                  {info?.balance_mode
                    ? `Paga saldo ${EUR(info.price_total_with_vat)} con Stripe (carta di credito/debito)`
                    : `Paga ora ${EUR(info.price_total_with_vat)} con Stripe`}
                </Button>
              ) : (
                // Iter137: tasto Conferma esplicito per bonifico/offline.
                // Iter140w: apre prima il modal di riepilogo, poi al conferma
                // esegue submitOffline().
                <Button
                  onClick={() => setConfirmingMethod(currentMethod)}
                  disabled={submitting || !clientEmail.trim() || (info?.service?.has_questionnaire && (() => {
                    const fields = info.service.questionnaire_fields || [];
                    if (fields.length === 0) return !questionnaireAnswer.trim();
                    return fields.some((f) => {
                      if (!f.required) return false;
                      const v = questionnaireAnswers[f.id];
                      if (f.type === "checkbox") return !(Array.isArray(v) && v.length > 0);
                      return !(v && String(v).trim());
                    });
                  })())}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 border-0 py-6 text-base font-semibold text-white disabled:opacity-60"
                  data-testid="checkout-confirm-order-btn"
                >
                  {info?.balance_mode
                    ? (currentMethod?.type === "bank_transfer"
                      ? `Paga saldo con Bonifico — ${EUR(info.price_total_with_vat)}`
                      : currentMethod?.type === "cash"
                        ? `Paga saldo in contanti al ritiro — ${EUR(info.price_total_with_vat)}`
                        : `Paga saldo con ${currentMethod?.name || "il metodo scelto"} — ${EUR(info.price_total_with_vat)}`)
                    : `Conferma ordine — ${EUR(info.price_total_with_vat)}`}
                </Button>
              );
            })()}
            {!offlineConfirmed && ((info?.payment_methods || []).find((m) => m.id === selectedMethod)?.type || "stripe") === "stripe" && (
              <div className="text-[11px] text-center text-slate-500" data-testid="checkout-stripe-footer">Pagamento sicuro tramite Stripe · Nessun dato di carta sul nostro server</div>
            )}
            <button
              type="button"
              onClick={() => setPhase("info")}
              className="w-full text-xs text-slate-500 hover:text-slate-700 pt-2"
              data-testid="checkout-back-info"
            >
              ← Torna alle informazioni
            </button>
          </div>
        )}

        {/* FASE 3 - CALENDARIO: prenota slot dopo pagamento */}
        {/* Iter138: nascosta se il cliente ha confermato con metodo NON-Stripe
            (`offlineConfirmed`). Per queste pratiche non serve prenotare slot. */}
        {phase === "calendar" && info.visit_on_first_step && !info.visit_skipped && !offlineConfirmed && (
          <div className="bg-white border rounded-lg shadow-sm overflow-hidden" data-testid="checkout-calendar">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-purple-50/60">
              <div>
                <div className="text-xs uppercase tracking-wider text-purple-800 font-bold">Prenota il sopralluogo</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {totalSlotsCount > 0 ? `${totalSlotsCount} slot disponibili questo mese` : "Nessuno slot disponibile"}
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <button type="button" onClick={() => { setSlotMonth(m => ({ year: m.month === 1 ? m.year - 1 : m.year, month: m.month === 1 ? 12 : m.month - 1 })); setSelectedDate(null); }} className="w-7 h-7 flex items-center justify-center border rounded hover:bg-white" data-testid="checkout-month-prev">‹</button>
                <span className="font-mono font-semibold min-w-[110px] text-center uppercase text-slate-700 tracking-wider">{MONTHS[slotMonth.month - 1]} {slotMonth.year}</span>
                <button type="button" onClick={() => { setSlotMonth(m => ({ year: m.month === 12 ? m.year + 1 : m.year, month: m.month === 12 ? 1 : m.month + 1 })); setSelectedDate(null); }} className="w-7 h-7 flex items-center justify-center border rounded hover:bg-white" data-testid="checkout-month-next">›</button>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-0">
              <div className="col-span-3 p-4 border-r">
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-widest text-slate-400 mb-1">
                  <div>L</div><div>M</div><div>M</div><div>G</div><div>V</div><div>S</div><div>D</div>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-sm">
                  {monthGrid.map((c, i) => {
                    if (!c) return <div key={`e${i}`} className="py-2" />;
                    const active = selectedDate === c.date;
                    const disabled = !c.hasSlots;
                    return (
                      <button key={c.date} type="button"
                        disabled={disabled}
                        onClick={() => { setSelectedDate(c.date); setChosenSlot(null); }}
                        data-testid={`checkout-day-${c.date}`}
                        className={`relative py-2 rounded-full transition-all
                          ${active ? "ht-gradient text-white font-bold shadow" : ""}
                          ${!active && c.hasSlots ? "hover:bg-purple-100 text-slate-800 font-medium cursor-pointer" : ""}
                          ${disabled ? "text-slate-300 cursor-not-allowed" : ""}`}>
                        {c.day}
                        {!active && c.hasSlots && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-purple-600 rounded-full" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="col-span-2 p-4 bg-slate-50/50">
                {!selectedDate ? (
                  <div className="text-center py-10 text-xs text-slate-500">Seleziona una data<br/>a sinistra</div>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-slate-800 capitalize">
                      {new Date(selectedDate).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                    </div>
                    <div className="text-[11px] text-slate-500 mb-3">Scegli l'orario</div>
                    <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
                      {slotsForSelectedDate.map(s => {
                        const slotMin = Number(s.minute || 0);
                        const slotKey = `${s.date}_${s.hour}_${slotMin}`;
                        const active = chosenSlot?.date === s.date
                          && chosenSlot?.hour === s.hour
                          && Number(chosenSlot?.minute || 0) === slotMin;
                        return (
                          <button key={slotKey} type="button" onClick={() => setChosenSlot(s)}
                            className={`p-2 rounded text-sm border-2 transition-all font-mono
                              ${active ? "ht-gradient border-0 text-white font-semibold shadow" : "bg-white border-slate-200 hover:border-purple-400"}`}
                            data-testid={`checkout-hour-${slotKey}`}>
                            {String(s.hour).padStart(2, "0")}:{String(slotMin).padStart(2, "0")}
                          </button>
                        );
                      })}
                    </div>
                    {chosenSlot && chosenSlot.date === selectedDate && (
                      <div className="mt-3 p-2.5 bg-purple-50 border border-purple-200 rounded text-xs" data-testid="checkout-chosen-slot">
                        <div className="text-purple-800 font-semibold">Data confermata: {chosenSlot.date} · {String(chosenSlot.hour).padStart(2,"0")}:{String(Number(chosenSlot.minute || 0)).padStart(2,"0")}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="p-4 border-t bg-white">
              <Button
                onClick={bookSlot}
                disabled={!chosenSlot || bookingSlot}
                className="w-full ht-gradient border-0 py-5 text-base font-semibold"
                data-testid="checkout-book-slot-btn"
              >
                {bookingSlot ? "Prenotazione in corso…" : "Conferma sopralluogo"}
              </Button>
            </div>
          </div>
        )}

        <div className="text-center text-xs text-slate-400 pt-4">
          Contatta il professionista: <a className="text-purple-700 hover:underline" href={`mailto:${info.professional.email}`}>{info.professional.email}</a>
        </div>

        {/* Iter140w: modal di conferma pre-submit. Chiede "Confermi di voler
            procedere con [Metodo]?" prima di lanciare submit/submitOffline. */}
        {confirmingMethod && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => !submitting && setConfirmingMethod(null)}
            data-testid="checkout-confirm-modal"
          >
            <div
              className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center space-y-2">
                <div className="text-4xl">💳</div>
                <h3 className="text-lg font-bold text-slate-900">Conferma pagamento</h3>
                <p className="text-sm text-slate-600">
                  Confermi di voler procedere con
                  <br />
                  <strong className="text-slate-900">{confirmingMethod.name || confirmingMethod.type}</strong>?
                </p>
                <div className="pt-2 text-lg font-bold text-purple-800">
                  {EUR(info.price_total_with_vat)}
                  <span className="block text-[11px] font-normal text-slate-500">
                    (IVA 22% inclusa)
                  </span>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmingMethod(null)}
                  disabled={submitting}
                  className="flex-1"
                  data-testid="checkout-confirm-cancel"
                >
                  Annulla
                </Button>
                <Button
                  onClick={async () => {
                    const isStripe = !confirmingMethod || confirmingMethod.type === "stripe";
                    setConfirmingMethod(null);
                    if (isStripe) await submit(); else await submitOffline();
                  }}
                  disabled={submitting}
                  className="flex-1 ht-gradient border-0 text-white"
                  data-testid="checkout-confirm-proceed"
                >
                  {submitting ? "Attendere…" : "Sì, procedi"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}


// ==================== QuestionnaireFieldInput (Iter64) ====================
// Renderizza un singolo campo del questionario cliente in base al type
// (text / textarea / radio / checkbox / select). Il valore è una stringa
// per tutti i tipi tranne "checkbox" che è List[str].
function QuestionnaireFieldInput({ field, value, onChange }) {
  const opts = field.options || [];
  const req = !!field.required;
  const testId = `q-field-${field.id}`;

  if (field.type === "textarea") {
    return (
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {field.label} {req && <span className="text-red-600">*</span>}
        </label>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          maxLength={5000}
          className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          data-testid={testId}
          required={req}
        />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {field.label} {req && <span className="text-red-600">*</span>}
        </label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          data-testid={testId}
        >
          <option value="">— Seleziona —</option>
          {opts.map((o, i) => <option key={i} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "radio") {
    return (
      <div>
        <div className="text-xs font-semibold text-slate-700">
          {field.label} {req && <span className="text-red-600">*</span>}
        </div>
        <div className="mt-1 space-y-1">
          {opts.map((o, i) => (
            <label key={i} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`${testId}-opt-${i}`}>
              <input
                type="radio"
                name={field.id}
                value={o}
                checked={value === o}
                onChange={() => onChange(o)}
              />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "checkbox") {
    const list = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      const next = list.includes(o) ? list.filter(x => x !== o) : [...list, o];
      onChange(next);
    };
    return (
      <div>
        <div className="text-xs font-semibold text-slate-700">
          {field.label} {req && <span className="text-red-600">*</span>}
        </div>
        <div className="mt-1 space-y-1">
          {opts.map((o, i) => (
            <label key={i} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`${testId}-opt-${i}`}>
              <input type="checkbox" checked={list.includes(o)} onChange={() => toggle(o)} />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  // Default: text
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700">
        {field.label} {req && <span className="text-red-600">*</span>}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        data-testid={testId}
        required={req}
      />
    </div>
  );
}

